import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { AppDispatch } from '@/Redux/ConfigureStore';
import { useDispatch } from 'react-redux';
import { uploadTemplatesDataActions } from '@/Redux/DynamicData/UploadTemplatesData/reducer';

export function useUploadTemplatesSettings() {
  const dispatch: AppDispatch = useDispatch();

  const {
    tours: {
      AccountingSettings: { UploadTemplates, UploadTemplatesModal },
    },
  } = useLocalization();

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: UploadTemplates.Title,
      steps: [
        {
          content: UploadTemplates.Step1.Text,
          placement: 'auto',
          target: '#uploadTemplates',
          title: UploadTemplates.Step1.Title,
          disableBeacon: true,
          link: UploadTemplates.Step1.DetailsLink,
        },
        {
          content: UploadTemplates.Step2.Text,
          placement: 'auto',
          target: '#templatesTable',
          title: UploadTemplates.Step2.Title,
          disableBeacon: true,
          link: UploadTemplates.Step2.DetailsLink,
        },
        {
          content: UploadTemplates.Step3.Text,
          placement: 'auto',
          target: '#crudExtended',
          title: UploadTemplates.Step3.Title,
          disableBeacon: true,
          link: UploadTemplates.Step3.DetailsLink,
        },
        {
          content: UploadTemplates.Step4.Text,
          placement: 'auto',
          target: '.dx-toolbar-after',
          title: UploadTemplates.Step4.Title,
          disableBeacon: true,
          link: UploadTemplates.Step4.DetailsLink,
          spotlightPadding: 0,
          styles: {
            spotlight: {
              maxWidth: '500px',
            },
          },
        },
        {
          content: UploadTemplates.Step5.Text,
          placement: 'auto',
          target: '#tableImportButton',
          title: UploadTemplates.Step5.Title,
          disableBeacon: true,
          link: UploadTemplates.Step5.DetailsLink,
          styles: {
            spotlight: {
              minWidth: '65px',
            },
          },
        },
        {
          content: UploadTemplates.Step6.Text,
          placement: 'auto',
          target: '#templateInfo',
          title: UploadTemplates.Step6.Title,
          disableBeacon: true,
          link: UploadTemplates.Step6.DetailsLink,
        },
        {
          content: UploadTemplates.Step7.Text,
          placement: 'auto',
          target: '#generalInfo',
          title: UploadTemplates.Step7.Title,
          disableBeacon: true,
          link: UploadTemplates.Step7.DetailsLink,
        },
        {
          content: UploadTemplates.Step8.Text,
          placement: 'auto',
          target: '#accountChartInfo',
          title: UploadTemplates.Step8.Title,
          disableBeacon: true,
          link: UploadTemplates.Step8.DetailsLink,
        },
        {
          content: UploadTemplates.Step9.Text,
          placement: 'auto',
          target: '#intraCommunityInfo',
          title: UploadTemplates.Step9.Title,
          disableBeacon: true,
          link: UploadTemplates.Step9.DetailsLink,
        },
      ],
    }),
    [UploadTemplates],
  );

  const templateSettingsTour = useMemo<TourType>(
    () => ({
      id: 'templateSettingsTour',
      title: UploadTemplatesModal.Title,
      before: () => {
        dispatch(uploadTemplatesDataActions.setUploadTemplateModal({ visible: true }));
      },
      steps: [
        {
          content: UploadTemplatesModal.Step1.Text,
          placement: 'auto',
          target: '#modalTitle',
          title: UploadTemplatesModal.Step1.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step1.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step2.Text,
          placement: 'auto',
          target: '#isForAutoSave',
          title: UploadTemplatesModal.Step2.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step2.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step3.Text,
          placement: 'auto',
          target: '#templateMainForm',
          title: UploadTemplatesModal.Step3.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step3.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step4.Text,
          placement: 'auto',
          target: '#introCommunityForm',
          title: UploadTemplatesModal.Step4.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step4.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step5.Text,
          placement: 'auto',
          target: '#standardAccountChart',
          title: UploadTemplatesModal.Step5.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step5.DetailsLink,
          styles: {
            spotlight: {
              marginTop: '-25px',
            },
          },
        },
        {
          content: UploadTemplatesModal.Step6.Text,
          placement: 'auto',
          target: '#addNewScenario',
          title: UploadTemplatesModal.Step6.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step6.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step7.Text,
          placement: 'auto',
          target: '#addScenarioForOtherCountries',
          title: UploadTemplatesModal.Step7.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step7.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step8.Text,
          placement: 'auto',
          target: '#accountChartCrud',
          title: UploadTemplatesModal.Step8.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step8.DetailsLink,
        },
        {
          content: UploadTemplatesModal.Step9.Text,
          placement: 'auto',
          target: '#modalNavButtons',
          title: UploadTemplatesModal.Step9.Title,
          disableBeacon: true,
          link: UploadTemplatesModal.Step9.DetailsLink,
        },
      ],
    }),
    [UploadTemplatesModal],
  );

  return [pageTour, templateSettingsTour];
}
